import React from 'react';
import { Wrench, Monitor } from 'lucide-react';

// =====================================================================
// Chamados — a PORTARIA do módulo (escolha de departamento).
//
// Modal BLOQUEANTE que pergunta o departamento antes de qualquer coisa:
//   T.I        → leva a ABRIR CHAMADO (com o GPS-TI no topo, como Passo 1)
//   Manutenção → leva a ABRIR CHAMADO na fila da Manutenção (17/09/2026)
//
// ⚠️⚠️ A OPÇÃO *MANUTENÇÃO* SÓ APARECE PARA QUEM TOCA A IMPLANTAÇÃO — hoje um
//    usuário (`chamados-fila-manutencao.js`). A fila está pronta e NÃO no ar:
//    falta o cadastro dos equipamentos, e oferecer a opção antes disso levaria o
//    gerente a um formulário onde ele **não acha o equipamento dele**.
//    ⚠️ Com uma opção só, a pop-up deixa de ser pergunta e vira confirmação — e é
//    isso que ela mostra nesse caso (ver `soTemTI` abaixo). Manter a pergunta
//    "para qual área?" com uma resposta possível é o tipo de tela que ensina a
//    pessoa a clicar em qualquer coisa.
//
// ⚠️ A Manutenção deixou de ser um beco em 17/09/2026: até então ela exibia um
//    aviso de "em construção" e voltava para a escolha, porque o portal não
//    sabia abrir chamado fora da fila de TI. Com a fila aberta, o botão virou
//    uma porta como a outra — e a escolha aqui passou a CARREGAR A EQUIPE para
//    o formulário, em vez de só decidir onde a pessoa começa.
//
// ---------------------------------------------------------------------
// POR QUE ELA EXISTE (Julia, 2026-08-28)
// ---------------------------------------------------------------------
// Antes disto, quem abria o módulo caía na Visão Geral — fila, calendário dos
// técnicos e cards de navegação. Para a loja, isso é a resposta errada para a
// pergunta que ela tem: "meu KVS não liga, e agora?". O que ela quer está a dois
// cliques de distância, como aba lateral.
//
// ---------------------------------------------------------------------
// ⚠️⚠️ O DESTINO DO T.I MUDOU EM 31/08/2026 — era o MANUAL
// ---------------------------------------------------------------------
// A portaria nasceu (28/08) levando ao GUIA, e a inversão "manual primeiro,
// chamado depois" era o ponto dela. Três dias depois a Julia a reverteu: quem
// atravessa a portaria já decidiu abrir chamado, e o guia no caminho virava
// pedágio — um clique a mais para o ato que a pessoa veio fazer.
//
// ⚠️ A ORDEM NÃO SE PERDEU, mudou de lugar: a tela de abertura abre com o bloco
//    *Passo 1 — Abrir o GPS-TI*, no topo, antes do formulário. O guia continua
//    sendo a primeira coisa que se vê; ele só deixou de ser obrigatório para
//    chegar ao campo. Quem quiser voltar a impor a ordem mexe no `onEscolherTI`
//    do `Chamados.js` — é uma linha, e o texto do botão abaixo tem de acompanhar.
//
// ---------------------------------------------------------------------
// ⚠️⚠️ ISTO É DESTINO DE ENTRADA, NUNCA PERMISSÃO
// ---------------------------------------------------------------------
// Quem enxerga o quê continua saindo INTEIRAMENTE da `PERMISSIONS` do
// `Chamados.js`, e o `useEffect` de falha fechada de lá segue mandando para a
// Visão Geral quem cair numa aba que não alcança. Esta tela só decide ONDE a
// pessoa começa. Depois de escolher, todas as abas que ela já podia ver
// continuam clicáveis — é PORTA, não trilho (decisão da Julia, 28/08).
//
// ⚠️ Não tem X e não fecha por clique no fundo, de propósito: "travada" foi o
//    pedido, e um modal de escolha que se fecha sozinho devolve a pessoa à tela
//    que a portaria existe para evitar.
//
// ⚠️ NADA de `localStorage` aqui. A pergunta volta a cada visita ao módulo
//    porque o estado vive no `Chamados.js` e morre com ele. Guardar a escolha
//    faria o comportamento divergir entre aparelhos e sumir em aba anônima — e
//    a portaria é barata: dois cliques que ensinam o caminho.
// =====================================================================

const COR = 'var(--ch-cor)';
const COR_ESCURA = 'var(--ch-cor-escura)';
const COR_CLARA = 'var(--ch-cor-clara)';

// ⚠️ `onEscolher` recebe a EQUIPE ('TI' | 'Manutencao') — é o mesmo vocabulário
//    da coluna `EQUIPE` do banco, de propósito: o valor atravessa a portaria, o
//    hub e o formulário até o `INSERT` sem ninguém traduzir no meio (tradução no
//    meio do caminho é onde nasce chamado na fila errada).
const ChamadosPortaria = ({ onEscolher, filas = ['TI'] }) => {
  // ⚠️ Quem decide a lista é o hub (`filasVisiveis`), não esta tela: ela é a
  //    porta, e a régua de acesso mora num lugar só.
  const soTemTI = !filas.includes('Manutencao');

  const fundo = {
    position: 'fixed', inset: 0, zIndex: 1000,
    backgroundColor: 'rgba(15, 23, 42, 0.55)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
  };

  const caixa = {
    backgroundColor: '#fff', borderRadius: 16, width: '100%', maxWidth: 520,
    border: '1px solid #e2e8f0', boxShadow: '0 20px 50px rgba(15, 23, 42, 0.25)',
    overflow: 'hidden',
  };

  const topo = {
    background: `linear-gradient(135deg, ${COR} 0%, ${COR_ESCURA} 100%)`,
    padding: '20px 24px', color: '#fff',
  };

  const botao = {
    display: 'flex', alignItems: 'center', gap: 14, width: '100%',
    padding: '18px 20px', borderRadius: 12, border: '1px solid #e2e8f0',
    backgroundColor: '#fff', cursor: 'pointer', textAlign: 'left',
    transition: 'border-color .15s, background-color .15s',
  };

  return (
    <div style={fundo}>
      {/* ⚠️ Sem onClick no fundo: clicar fora NÃO fecha (ver comentário do topo). */}
      <div style={caixa} role="dialog" aria-modal="true" aria-labelledby="portaria-titulo">
        <div style={topo}>
          <h2 id="portaria-titulo" style={{ margin: 0, fontSize: 20, fontWeight: 800 }}>
            {soTemTI ? 'Abrir chamado' : 'Abrir chamado para qual área?'}
          </h2>
          <p style={{ margin: '6px 0 0 0', fontSize: 13, opacity: 0.9, lineHeight: 1.5 }}>
            {soTemTI
              ? 'Os chamados de T.I do portal — POS, KVS, impressora, rede, pinpad.'
              : 'Escolha o time que cuida do que está com problema.'}
          </p>
        </div>

        <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <button
              type="button"
              onClick={() => onEscolher('TI')}
              style={botao}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = COR; e.currentTarget.style.backgroundColor = COR_CLARA; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.backgroundColor = '#fff'; }}
            >
              <span style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: 44, height: 44, borderRadius: 10, backgroundColor: COR_CLARA, flexShrink: 0,
              }}>
                <Monitor size={22} color={COR} />
              </span>
              <span>
                <span style={{ display: 'block', fontSize: 15, fontWeight: 700, color: '#0f172a' }}>T.I</span>
                {/* ⚠️⚠️ O TEXTO SEGUE O DESTINO, e o destino mudou em 31/08/2026:
                    leva ao FORMULÁRIO, não mais ao guia. Antes ele avisava o
                    contrário, para quem clicasse não achar que errou o botão — o
                    aviso continua existindo pelo mesmo motivo, só que ao
                    contrário.
                    ⚠️ O guia é citado porque ele abre no topo da própria tela de
                    abertura (*Passo 1*): a ordem "consulte antes" é ensinada ali,
                    e prometer aqui um guia que não é o destino repetiria o
                    problema que este comentário existe para evitar. */}
                <span style={{ display: 'block', fontSize: 12.5, color: '#64748b', marginTop: 2, lineHeight: 1.5 }}>
                  POS, KVS, impressora, rede, pinpad… Abre o <strong>formulário de chamado</strong>,
                  com o <strong>guia de suporte</strong> logo no topo — vale consultar antes,
                  porque ele resolve boa parte na hora.
                </span>
              </span>
            </button>

            {/* ⚠️ Ausente, e não desabilitado: botão apagado numa pop-up bloqueante
                vira pergunta sem resposta para a loja, que não tem como saber
                quando ele acende. Quando a fila abrir, ele aparece para todos. */}
            {!soTemTI && (
            <button
              type="button"
              onClick={() => onEscolher('Manutencao')}
              style={botao}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = COR; e.currentTarget.style.backgroundColor = COR_CLARA; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.backgroundColor = '#fff'; }}
            >
              <span style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: 44, height: 44, borderRadius: 10, backgroundColor: COR_CLARA, flexShrink: 0,
              }}>
                <Wrench size={22} color={COR} />
              </span>
              <span>
                <span style={{ display: 'block', fontSize: 15, fontWeight: 700, color: '#0f172a' }}>Manutenção</span>
                <span style={{ display: 'block', fontSize: 12.5, color: '#64748b', marginTop: 2, lineHeight: 1.5 }}>
                  Fritadeira, chapa, ar-condicionado, prédio. Abre o
                  {' '}<strong>formulário de chamado</strong> para a equipe de Manutenção.
                </span>
              </span>
            </button>
            )}
        </div>
      </div>
    </div>
  );
};

export default ChamadosPortaria;
